
export interface StreamCallbacks<T> {
    onEvent: (eventType: string, data: any) => void;
    onComplete: (count: number) => void;
    onError: (error: string) => void;
}

/**
 * 通用 SSE 流式请求工具
 */
export const fetchSSE = <T>(
    url: string,
    data: any,
    callbacks: StreamCallbacks<T>
): (() => void) => {
    const controller = new AbortController();

    // 获取CSRF token
    const getCsrfToken = (): string => {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === 'csrftoken') {
                return value;
            }
        }
        return '';
    };

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
        },
        body: JSON.stringify(data),
        credentials: 'include',
        signal: controller.signal,
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error('无法获取响应流');
            }

            const decoder = new TextDecoder();
            let buffer = '';
            let receivedDone = false;

            const parseSSELine = (line: string) => {
                if (line.startsWith('data: ')) {
                    try {
                        const jsonStr = line.slice(6);
                        if (jsonStr.trim()) {
                            const event = JSON.parse(jsonStr);

                            if (event.type === 'done') {
                                receivedDone = true;
                                callbacks.onComplete(event.count);
                            } else if (event.type === 'error') {
                                callbacks.onError(event.message);
                            } else {
                                // 传递其它类型的事件
                                callbacks.onEvent(event.type, event.data);
                            }
                        }
                    } catch (e) {
                        console.warn('SSE解析错误:', e, line);
                    }
                }
            };

            const processStream = async () => {
                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        // 流结束时，处理buffer中剩余的数据
                        if (buffer.trim()) {
                            parseSSELine(buffer);
                        }
                        // 如果没有收到done事件，手动触发完成
                        if (!receivedDone) {
                            console.log('SSE流结束但未收到done事件，手动触发完成');
                            callbacks.onComplete(0);
                        }
                        break;
                    }

                    buffer += decoder.decode(value, { stream: true });

                    // 解析SSE事件
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        parseSSELine(line);
                    }
                }
            };

            processStream().catch(err => {
                if (err.name !== 'AbortError') {
                    callbacks.onError(err.message);
                }
            });
        })
        .catch(err => {
            if (err.name !== 'AbortError') {
                callbacks.onError(err.message);
            }
        });

    // 返回取消函数
    return () => controller.abort();
};
